import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  listenerProfile,
  profileSections,
  listenerStatuses,
  type ProfileSectionId,
} from '@/mocks/rfwListenerSocialProfile';

type TabView = 'actividad' | 'clubes' | 'logros' | 'guardados' | 'eventos' | 'concursos' | 'seguidores' | 'siguiendo';

const sectionConfig: { id: TabView; label: string; icon: string; sectionId: ProfileSectionId }[] = [
  { id: 'actividad', label: 'Actividad', icon: 'ri-flashlight-line', sectionId: 'actividad' },
  { id: 'clubes', label: 'Clubes', icon: 'ri-group-line', sectionId: 'clubes' },
  { id: 'logros', label: 'Logros', icon: 'ri-medal-2-line', sectionId: 'logros' },
  { id: 'guardados', label: 'Guardados', icon: 'ri-bookmark-line', sectionId: 'guardados' },
  { id: 'eventos', label: 'Eventos', icon: 'ri-calendar-event-line', sectionId: 'eventos' },
  { id: 'concursos', label: 'Concursos', icon: 'ri-trophy-line', sectionId: 'concursos' },
  { id: 'seguidores', label: 'Seguidores', icon: 'ri-user-heart-line', sectionId: 'seguidores' },
  { id: 'siguiendo', label: 'Siguiendo', icon: 'ri-user-star-line', sectionId: 'siguiendo' },
];

export default function PerfilSocialOyentePage() {
  const [activeTab, setActiveTab] = useState<TabView>('actividad');
  const profile = listenerProfile;

  const sectionCounts: Record<TabView, number> = {
    actividad: profile.actividad.length,
    clubes: profile.clubes.length,
    logros: profile.logros.length,
    guardados: profile.guardados.length,
    eventos: profile.eventos.length,
    concursos: profile.concursos.length,
    seguidores: profile.stats.followers,
    siguiendo: profile.stats.following,
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero / Cover Area */}
      <section className="relative">
        <div className="h-48 md:h-72 relative overflow-hidden">
          <img src={profile.coverUrl} alt={`Portada de ${profile.name}`} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/5 to-black/60"></div>
        </div>

        {/* Profile Info Overlay */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 relative -mt-20 md:-mt-24 z-10">
          <div className="flex flex-col md:flex-row md:items-end gap-4 md:gap-6">
            <img
              src={profile.photoUrl}
              alt={profile.name}
              className="w-28 h-28 md:w-36 md:h-36 rounded-full border-4 border-background-50 object-cover object-top ring-2 ring-pink-200 shrink-0"
            />
            <div className="flex-1 pt-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl md:text-3xl font-bold text-white drop-shadow-md">{profile.name}</h1>
                <span className="text-sm text-white/80">{profile.username}</span>
              </div>
              <p className="text-xs text-white/70 mt-0.5">
                <i className="ri-map-pin-line text-[10px] mr-0.5"></i>
                {profile.city}, {profile.country} {profile.countryFlag}
                <span className="mx-1.5">·</span>
                <i className="ri-calendar-line text-[10px] mr-0.5"></i>
                Oyente desde {new Date(profile.joinDate).toLocaleDateString('es-MX', { year: 'numeric', month: 'long' })}
              </p>
              <p className="text-sm text-white/80 mt-2 line-clamp-2 max-w-xl">{profile.bio}</p>

              {/* Statuses Row */}
              <div className="flex flex-wrap items-center gap-1.5 mt-3">
                {listenerStatuses.map((s) => (
                  <span
                    key={s.id}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold ${s.bgColor} ${s.textColor} border ${s.ringColor} ring-1`}
                  >
                    <i className={`${s.icon} text-[9px]`}></i>
                    {s.name}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Level Bar */}
          <div className="mt-4 p-3 rounded-xl bg-white/10 backdrop-blur-md border border-white/10">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-white">{profile.level.name}</span>
                <span className="text-[10px] text-white/60">Nivel {profile.level.number}</span>
              </div>
              <span className="text-[10px] text-white/70">{profile.level.pointsToNext} pts para {profile.level.nextLevel}</span>
            </div>
            <div className="h-2 rounded-full bg-white/20 overflow-hidden">
              <div className="h-full rounded-full bg-white transition-all duration-500" style={{ width: `${profile.level.progress}%` }}></div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 mt-4">
            {[
              { label: 'Ranking', value: `#${profile.stats.ranking}`, sub: 'Global' },
              { label: 'Puntos', value: profile.stats.totalPoints.toLocaleString(), sub: 'Score RFW' },
              { label: 'Seguidores', value: profile.stats.followers.toLocaleString(), sub: '' },
              { label: 'Siguiendo', value: profile.stats.following.toString(), sub: '' },
              { label: 'Días Activo', value: profile.stats.diasActivo.toLocaleString(), sub: '' },
              { label: 'Racha', value: `${profile.stats.rachaDias} días`, sub: 'Actual' },
            ].map((stat, i) => (
              <div key={i} className="p-2.5 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 text-center">
                <p className="text-base md:text-lg font-bold text-white">{stat.value}</p>
                <p className="text-[9px] text-white/60">{stat.label}</p>
                {stat.sub && <p className="text-[8px] text-white/40">{stat.sub}</p>}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom gradient spacer */}
        <div className="h-8 bg-gradient-to-b from-transparent to-background-50 -mt-3 relative z-0"></div>
      </section>

      {/* Section Navigation */}
      <div className="sticky top-0 z-30 bg-background-50/80 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 overflow-x-auto no-scrollbar">
          <div className="flex items-center gap-1 py-3 min-w-max">
            {sectionConfig.map((sec) => (
              <button
                key={sec.id}
                onClick={() => setActiveTab(sec.id)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === sec.id
                    ? 'bg-pink-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${sec.icon} text-[10px]`}></i>
                {sec.label}
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${activeTab === sec.id ? 'bg-white/20 text-white' : 'bg-background-100 text-foreground-400'}`}>
                  {sectionCounts[sec.id]}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Badges Row */}
        <div className="flex flex-wrap items-center gap-1.5 mb-6">
          {profile.badges.map((badge) => (
            <span key={badge.name} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold ${badge.bgColor} ${badge.color}`}>
              <i className={`${badge.icon} text-[9px]`}></i>
              {badge.name}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar: Section Overview */}
          <div className="lg:col-span-1 order-last lg:order-first">
            <div className="sticky top-24 space-y-4">
              <div className="p-4 rounded-2xl bg-background-100 border border-background-200/70">
                <h4 className="text-xs font-bold text-foreground-950 mb-3">Tu Perfil en Números</h4>
                <div className="space-y-2.5">
                  {profileSections.map((section) => (
                    <div key={section.id} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-full ${section.bgColor} flex items-center justify-center`}>
                          <i className={`${section.icon} ${section.color} text-xs`}></i>
                        </div>
                        <span className="text-[11px] text-foreground-600">{section.name}</span>
                      </div>
                      <span className="text-xs font-bold text-foreground-950">{section.count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Statuses Pills */}
              <div className="p-4 rounded-2xl bg-background-100 border border-background-200/70">
                <h4 className="text-xs font-bold text-foreground-950 mb-3">Tus Estatus</h4>
                <div className="space-y-2">
                  {listenerStatuses.map((s) => (
                    <div key={s.id} className={`flex items-center gap-2 p-2 rounded-xl ${s.bgColor}`}>
                      <div className={`w-7 h-7 rounded-full ${s.color} flex items-center justify-center`}>
                        <i className={`${s.icon} text-white text-xs`}></i>
                      </div>
                      <div className="min-w-0">
                        <p className={`text-[10px] font-bold ${s.textColor}`}>{s.name}</p>
                        <p className="text-[8px] text-foreground-500">{s.level}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Actividad */}
            {activeTab === 'actividad' && (
              <div className="space-y-3">
                {profile.actividad.map((item) => (
                  <Link
                    key={item.id}
                    to={item.link}
                    className="flex items-start gap-3 p-4 rounded-2xl bg-background-50 border border-background-200/70 hover:border-foreground-300/50 transition-all duration-200 cursor-pointer"
                  >
                    <div className={`w-9 h-9 rounded-full ${item.color} flex items-center justify-center shrink-0`}>
                      <i className={`${item.icon} text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground-800 leading-relaxed">{item.description}</p>
                      <span className="text-[10px] text-foreground-400 mt-1 inline-block">{item.timestamp}</span>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-300 text-sm shrink-0 mt-2"></i>
                  </Link>
                ))}
              </div>
            )}

            {/* Clubes */}
            {activeTab === 'clubes' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {profile.clubes.map((club) => (
                  <div key={club.id} className="p-4 rounded-2xl bg-background-50 border border-background-200/70 hover:border-foreground-300/50 transition-all duration-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-violet-50 flex items-center justify-center">
                        <i className={`${club.icon} ${club.color} text-lg`}></i>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-foreground-950">{club.name}</p>
                        <p className="text-[10px] text-foreground-500">{club.members.toLocaleString()} miembros</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-medium text-foreground-600">{club.role}</span>
                      <span className="text-[9px] text-foreground-400">Desde {new Date(club.joinedDate).toLocaleDateString('es-MX', { year: 'numeric', month: 'short' })}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Logros */}
            {activeTab === 'logros' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {profile.logros.map((logro) => (
                  <div key={logro.id} className="flex items-start gap-3 p-4 rounded-2xl bg-background-50 border border-background-200/70">
                    <div className={`w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center shrink-0`}>
                      <i className={`${logro.icon} text-amber-600 text-lg`}></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-bold text-foreground-950">{logro.name}</p>
                        <span className={`text-[9px] font-bold ${logro.tierColor}`}>{logro.tier}</span>
                      </div>
                      <p className="text-[10px] text-foreground-500">{logro.description}</p>
                      <p className="text-[9px] text-foreground-400 mt-1">{logro.earnedDate}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Guardados */}
            {activeTab === 'guardados' && (
              <div className="space-y-3">
                {profile.guardados.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 p-4 rounded-2xl bg-background-50 border border-background-200/70">
                    <div className="w-9 h-9 rounded-full bg-teal-50 flex items-center justify-center shrink-0">
                      <i className={`${item.typeIcon} text-teal-600 text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground-950 line-clamp-1">{item.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-foreground-500">{item.type}</span>
                        <span className="text-[9px] text-foreground-400">{item.savedDate}</span>
                        {item.isFavorite && (
                          <span className="text-[9px] text-amber-600"><i className="ri-star-fill text-[8px]"></i> Favorito</span>
                        )}
                      </div>
                    </div>
                    <Link to="/guardados" className="text-[10px] text-pink-600 font-medium hover:underline whitespace-nowrap">Ver todos</Link>
                  </div>
                ))}
              </div>
            )}

            {/* Eventos */}
            {activeTab === 'eventos' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {profile.eventos.map((evento) => (
                  <div key={evento.id} className={`p-4 rounded-2xl border transition-all duration-200 ${evento.status === 'upcoming' ? 'bg-rose-50 border-rose-100/50' : 'bg-background-50 border-background-200/70 opacity-60'}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${evento.status === 'upcoming' ? 'bg-rose-200' : 'bg-background-100'}`}>
                        <i className={`${evento.icon} text-rose-600 text-sm`}></i>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-foreground-950">{evento.title}</p>
                        <div className="flex items-center gap-2 text-[10px] text-foreground-500">
                          <span>{evento.date}</span>
                          <span>·</span>
                          <span>{evento.type}</span>
                        </div>
                      </div>
                    </div>
                    {evento.status === 'upcoming' && (
                      <span className="inline-block px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[9px] font-semibold">Próximo</span>
                    )}
                    {evento.status === 'past' && (
                      <span className="inline-block px-2 py-0.5 rounded-full bg-background-100 text-foreground-400 text-[9px] font-semibold">Asistido</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Concursos */}
            {activeTab === 'concursos' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {profile.concursos.map((concurso) => (
                  <div key={concurso.id} className="flex items-start gap-3 p-4 rounded-2xl bg-background-50 border border-background-200/70">
                    <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
                      <i className={`${concurso.icon} text-amber-600 text-sm`}></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-foreground-950">{concurso.title}</p>
                      <p className="text-[10px] text-foreground-500">{concurso.radio} · {concurso.date}</p>
                      <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-full text-[9px] font-semibold ${concurso.resultColor}`}>
                        {concurso.result}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Seguidores */}
            {activeTab === 'seguidores' && (
              <div className="space-y-3">
                {profile.followersList.map((follower) => (
                  <div key={follower.id} className="flex items-center gap-3 p-4 rounded-2xl bg-background-50 border border-background-200/70">
                    <img src={follower.photo} alt={follower.name} className="w-10 h-10 rounded-full object-cover object-top ring-1 ring-background-200/70 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-bold text-foreground-950">{follower.name}</p>
                        {follower.isMutual && (
                          <span className="text-[9px] text-pink-600 font-medium bg-pink-50 px-1.5 py-0.5 rounded-full">Mutuo</span>
                        )}
                      </div>
                      <p className="text-[10px] text-foreground-500">{follower.username}</p>
                      <div className="flex items-center gap-1 mt-1">
                        {follower.badges.map((b) => (
                          <span key={b} className="text-[8px] text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded-full">{b}</span>
                        ))}
                      </div>
                    </div>
                    <button className="px-3 py-1.5 rounded-full bg-pink-50 text-pink-600 text-[10px] font-semibold hover:bg-pink-100 transition-colors whitespace-nowrap cursor-pointer">
                      Seguir
                    </button>
                  </div>
                ))}
                <div className="text-center pt-4">
                  <Link to="/seguidores" className="text-sm text-pink-600 font-semibold hover:underline">
                    Ver los {profile.stats.followers.toLocaleString()} seguidores
                  </Link>
                </div>
              </div>
            )}

            {/* Siguiendo */}
            {activeTab === 'siguiendo' && (
              <div className="space-y-3">
                {profile.followingList.map((follow) => (
                  <div key={follow.id} className="flex items-center gap-3 p-4 rounded-2xl bg-background-50 border border-background-200/70">
                    <div className="relative shrink-0">
                      <img src={follow.photo} alt={follow.name} className="w-10 h-10 rounded-full object-cover object-top ring-1 ring-background-200/70" />
                      {follow.isLive && (
                        <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-background-50 flex items-center justify-center">
                          <span className="w-1.5 h-1.5 rounded-full bg-white"></span>
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-foreground-950">{follow.name}</p>
                      <p className="text-[10px] text-foreground-500">{follow.username}</p>
                      {follow.radioName && (
                        <p className="text-[9px] text-foreground-400 mt-0.5">{follow.radioName}</p>
                      )}
                    </div>
                    <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                      follow.type === 'radio' ? 'bg-indigo-50 text-indigo-600' :
                      follow.type === 'locutor' ? 'bg-sky-50 text-sky-600' :
                      'bg-pink-50 text-pink-600'
                    }`}>
                      {follow.type === 'radio' ? 'Radio' : follow.type === 'locutor' ? 'Locutor' : 'Oyente'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Cross Links */}
        <div className="mt-12 pt-8 border-t border-background-200/70">
          <p className="text-xs font-semibold text-foreground-400 mb-4 text-center">EXPLORÁ TAMBIÉN</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { to: '/rankings', label: 'Rankings', icon: 'ri-bar-chart-line' },
              { to: '/insignias', label: 'Insignias', icon: 'ri-award-line' },
              { to: '/logros', label: 'Logros', icon: 'ri-medal-2-line' },
              { to: '/top-fans', label: 'Top Fans', icon: 'ri-fire-line' },
              { to: '/vip-rfw', label: 'VIP RFW', icon: 'ri-vip-crown-line' },
              { to: '/embajadores', label: 'Embajadores', icon: 'ri-shield-star-line' },
            ].map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-600 hover:text-foreground-950 hover:bg-background-200 text-xs font-medium transition-all duration-200 whitespace-nowrap"
              >
                <i className={`${link.icon} text-[10px]`}></i>
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Regla Final */}
        <div className="mt-8 p-6 rounded-2xl bg-pink-100/70 border border-pink-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-pink-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-user-star-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            El perfil debe sentirse importante.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            Porque tu identidad en la comunidad importa. Cada insignia que ganaste, cada club al que pertenecés, cada concurso en el que participaste, cada evento al que asististe — todo construye quién sos como oyente. Tu perfil no es solo una página, es tu historia en la radio. Y debe sentirse tan importante como vos.
          </p>
        </div>
      </div>
    </div>
  );
}